import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose } from 'redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

import Button from '@material-ui/core/Button';
import Slider from '@material-ui/core/Slider';
import Snackbar from '@material-ui/core/Snackbar';
import Typography from '@material-ui/core/Typography';

import { Interweave } from 'interweave';
import CustomModal from '../../../components/Modals/Modal';
import { ModalCloseBtn } from '../../../components/statelessComponents/Buttons/ButtonsInModals';
import CircularProgressSmall from '../../../components/CircularProgressSmall';
import ContextProvider from '../../../context';
import messages from './messages';
import s from './LessonReviewModal.scss';

const LessonReviewModal = ({
  intl: { formatMessage },
  closeLessonReviewContent,
}) => {
  const { fetch } = useContext(ContextProvider);
  const lesson = useSelector(state => state.lessonDetails.lesson);
  const course = useSelector(state => state.courseDetails.course);
  const [hasReview, setHasReview] = useState(false);
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);
  const [showSnackbarMessage, setShowSnackbarMessage] = useState('');
  const [review, setReview] = useState({
    interestingQuestion: 3,
    challengingQuestion: 3,
    competentQuestion: 3,
  });
  const marks = {
    interestingQuestion: [
      {
        value: 1,
        label: formatMessage(messages.noLabel),
      },
      {
        value: 5,
        label: formatMessage(messages.yesLabel),
      },
    ],
    challengingQuestion: [
      {
        value: 1,
        label: formatMessage(messages.easyLabel),
      },
      {
        value: 5,
        label: formatMessage(messages.difficultLabel),
      },
    ],
    competentQuestion: [
      {
        value: 1,
        label: formatMessage(messages.noviceLabel),
      },
      {
        value: 5,
        label: formatMessage(messages.expertLabel),
      },
    ],
  };

  const questions = {
    interestingQuestion: formatMessage(messages.interestingQuestion),
    challengingQuestion: formatMessage(messages.challengingQuestion),
    competentQuestion: formatMessage(messages.competentQuestion),
  };

  useEffect(() => {
    setIsReviewLoading(true);
    fetch(`/api/lesson_reviews/${course._id}/${lesson._id}`, {
      method: 'GET',
    })
      .then(res =>
        res.json().then(resJson => {
          if (!res.ok) {
            throw new Error(resJson && resJson.message);
          }
          if (resJson && resJson.review) {
            setReview(_.omit(resJson.review, ['_id']));
            setHasReview(true);
          }
          setIsReviewLoading(false);
        }),
      )
      .catch(err => {
        setIsReviewLoading(false);
        setShowSnackbarMessage(err);
      });
  }, []);

  const handleReviewChange = name => (e, value) => {
    setReview(prevReview => ({
      ...prevReview,
      [name]: value,
    }));
  };

  const submitReview = () => {
    setIsReviewSubmitting(true);
    setShowSnackbarMessage('');
    fetch(`/api/lesson_reviews/${course._id}/${lesson._id}`, {
      method: 'PUT',
      body: JSON.stringify({ review }),
    })
      .then(async res => {
        if (!res.ok) {
          const resJson = await res.json();
          throw new Error(resJson && resJson.message);
        }
        setShowSnackbarMessage(formatMessage(messages.leaveRatingSuccess));
        setIsReviewSubmitting(false);
        setTimeout(() => {
          closeLessonReviewContent();
        }, 1000);
      })
      .catch(err => {
        setIsReviewSubmitting(false);
        setShowSnackbarMessage(err);
      });
  };

  return (
    <CustomModal
      isOpen
      overflowX
      isLoaderOn={isReviewLoading}
      width="fit-content"
      data-test="lesson-review-modal"
    >
      <div className={classNames(s.rateContentWrapper)}>
        <Typography variant="h5" component="h1" gutterBottom>
          <FormattedMessage {...messages.rateContentTitle} />
        </Typography>
        <Typography
          variant="subtitle1"
          classes={{ root: s.rateContentHeadings }}
        >
          <FormattedMessage {...messages.rateContentHeading} />
        </Typography>
        <div className={s.questionsWrapper}>
          {Object.keys(questions).map(key => (
            <React.Fragment key={key}>
              <Typography
                variant="h6"
                classes={{
                  root: classNames(s.rateContentHeadings, s.questionTitle),
                }}
              >
                {questions[key]}
              </Typography>
              <Slider
                min={1}
                max={5}
                value={review[key]}
                onChange={handleReviewChange(key)}
                disabled={isReviewLoading || isReviewSubmitting}
                marks={marks[key]}
                classes={{ markLabel: s.sliderMarkLabel }}
              />
            </React.Fragment>
          ))}
          <Button
            data-test-id="elwHcSHbOOtbID16rFhO0"
            fullWidth
            color="primary"
            variant="contained"
            data-test="submit-lesson-review-btn"
            className={s.submitBtn}
            onClick={submitReview}
            disabled={isReviewLoading || isReviewSubmitting}
          >
            {hasReview ? (
              <FormattedMessage {...messages.updateRatingLabel} />
            ) : (
              <FormattedMessage {...messages.submitRatingLabel} />
            )}
            {isReviewSubmitting && (
              <CircularProgressSmall wrapperClassName={s.btnLoader} />
            )}
          </Button>
        </div>
      </div>
      <ModalCloseBtn
        data-test-id="YyuOpfwEOVxFrLXvhUflh"
        fitMobileLandscapeFullWidth
        data-test="skip-lesson-review-btn"
        onClick={closeLessonReviewContent}
        disabled={isReviewLoading || isReviewSubmitting}
      />
      <Snackbar
        open={!!showSnackbarMessage}
        onClose={() => setShowSnackbarMessage('')}
        autoHideDuration={2000}
        message={<Interweave tagName="div" content={showSnackbarMessage} />}
      />
    </CustomModal>
  );
};

LessonReviewModal.propTypes = {
  intl: intlShape.isRequired,
  closeLessonReviewContent: PropTypes.func.isRequired,
};

export default compose(injectIntl, withStyles(s))(LessonReviewModal);
